import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { generateAuditedClient } from "../audit/auditedClient";
import { marked, parse } from "marked";
import { getSharedSnapshot } from "../graphql/queries";
import { addSharedConversation } from "../graphql/mutations";
import {
  AddSharedConversationInput,
  GetSharedSnapshotInput,
  SharedSnapshot,
  SharedSnapshotTurn,
} from "../models/API";
import { hexToString, isValidShareToken } from "./utils/utils";

marked.setOptions({ breaks: true, gfm: true });

interface SharedConversationPreviewProps {
  isOpen: boolean;
  menuWidth: number;
  userId: number;
  onConversationAdded?: (conversationId: string) => void;
}

// Read the share token from the URL query string (?view=shared&token=...).
function readTokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get("token") ?? "";
}

// Decode the hex-encoded AI response and strip the hidden SOURCES/TOOLCALLS
// markers the chat stores inline, mirroring ChatBoxComponent's read render.
function decodeResponse(response: string | null | undefined): string {
  if (!response) return "";
  let text = hexToString(response);
  text = text.replace(/\n<!-- SOURCES:(.*?) -->/s, "");
  text = text.replace(/\n<!-- TOOLCALLS:(.*?) -->/s, "");
  return text;
}

// Status -> static message for non-active snapshots. No conversation content,
// identifier, or token value is included (Req 8.5).
const STATUS_MESSAGES: Record<string, string> = {
  invalid: "This share link is invalid.",
  revoked: "This share link is no longer available.",
  unavailable: "This shared conversation is unavailable.",
};

const SharedConversationPreview: React.FC<SharedConversationPreviewProps> = ({
  isOpen,
  menuWidth,
  userId,
  onConversationAdded,
}) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>("loading");
  const [turns, setTurns] = useState<SharedSnapshotTurn[]>([]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const token = readTokenFromUrl();
  const client = generateAuditedClient();

  // Resolve the snapshot by token alone (Req 4.4). Malformed tokens are gated
  // client-side and never reach the backend (Req 8.3).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    async function loadSnapshot() {
      if (!isValidShareToken(token)) {
        if (!cancelled) {
          setStatus("invalid");
          setLoading(false);
        }
        return;
      }
      try {
        const input: GetSharedSnapshotInput = { share_token: token };
        const result = await client.graphql({
          query: getSharedSnapshot,
          variables: { input },
        });
        const snapshot: SharedSnapshot | null =
          result.data.getSharedSnapshot ?? null;
        if (cancelled) return;
        if (!snapshot) {
          setStatus("unavailable");
        } else {
          setStatus(snapshot.status);
          // Render every turn in ascending turn_seq (stored) order (Req 4.2).
          const ordered = [...(snapshot.turns ?? [])].sort(
            (a, b) => a.turn_seq - b.turn_seq
          );
          setTurns(ordered);
        }
      } catch (err) {
        console.error("[SharedConversationPreview] load failed:", err);
        if (!cancelled) setStatus("unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    setLoading(true);
    loadSnapshot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  // Create an independent Copy in the Recipient's own history (Req 5.1, 5.2).
  // On success navigate to the new conversation; on failure show an error and
  // create nothing (Req 5.6).
  const handleAdd = async () => {
    if (adding) return;
    setAdding(true);
    setAddError(null);
    try {
      const input: AddSharedConversationInput = {
        share_token: token,
        user_id: userId,
      };
      const result = await client.graphql({
        query: addSharedConversation,
        variables: { input },
      });
      const response = result.data.addSharedConversation;
      if (!response || !response.success) {
        throw new Error("Add to my conversations did not complete");
      }
      onConversationAdded?.(response.conversation_id);
    } catch (err) {
      console.error("[SharedConversationPreview] add failed:", err);
      setAddError(
        "Could not add this conversation to your history. Please try again."
      );
    } finally {
      setAdding(false);
    }
  };

  // Read-only render of a single turn: question as plain text, response as
  // markdown-parsed HTML, matching ChatBoxComponent (Req 4.2, 4.3).
  const renderTurn = (turn: SharedSnapshotTurn) => {
    const question = turn.question ? hexToString(turn.question) : "";
    const responseHtml = (
      parse(decodeResponse(turn.response)) as string
    ).replace(/>\s+</g, "><");
    return (
      <Box key={turn.turn_seq} sx={{ mb: 3 }}>
        {question && (
          <Box sx={{ fontWeight: 600, whiteSpace: "pre-wrap", mb: 1 }}>
            {question}
          </Box>
        )}
        <Box dangerouslySetInnerHTML={{ __html: responseHtml }} />
      </Box>
    );
  };

  if (!isOpen) return null;

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        ml: `${menuWidth}px`,
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Shared conversation
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={20} />
          <span>Loading shared conversation...</span>
        </Box>
      ) : status === "active" ? (
        <Box sx={{ maxWidth: 900 }}>
          {turns.map(renderTurn)}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={adding}
              startIcon={adding ? <CircularProgress size={16} /> : undefined}
            >
              Add to my conversations
            </Button>
            {addError && (
              <Typography variant="body2" sx={{ mt: 1, color: "error.main" }}>
                {addError}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        // Non-active states render no conversation content (Req 8.5).
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {STATUS_MESSAGES[status] ?? STATUS_MESSAGES.unavailable}
        </Typography>
      )}
    </Box>
  );
};

export default SharedConversationPreview;
